import { useEffect, useMemo, useRef, useState } from "react";
import { InfiniteData, useInfiniteQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { CountByAirStatusModel } from "../../../model/types";
import { AirOrderQuery, AirWhereQuery } from "../../query.types";
import { SavedAirResponseDTO } from "../../response.types";
import { useRequestCallbacks } from "shared/api/lib/api.hooks";
import { CARDS_REQUEST_LIMIT } from "shared/constants/common.constants";
import { Nullable } from "shared/lib/typeCheck.lib";

import { AirSavedCardModel } from "../../../model/model";
import { savedShipmentService } from "../../api";

type ParseResponseFuncReturnType = {
  page: AirSavedCardModel[];
  pages: AirSavedCardModel[][];
  totalCount: number;
  countByStatus: CountByAirStatusModel[];
};

const parseResponse = (response?: SavedAirResponseDTO[]): ParseResponseFuncReturnType => {
  const pages = response?.map((page) => page.cards.map((data) => new AirSavedCardModel(data)));

  return {
    pages: pages ?? [],
    page: pages ? pages[pages.length - 1] : [],
    totalCount: response?.[response.length - 1].total_count ?? 0,
    countByStatus: response?.[response.length - 1].count_by_status ?? [],
  };
};

export type RequestFuncParams = { where: AirWhereQuery; order: AirOrderQuery[] };

type SearchError = Error;

type RequestMoreCardsFunc = () => void;
type ResetCardsQueryFunc = () => Promise<void>;
type SetRequestFilterSyncFunc = (filter: Nullable<Partial<RequestFuncParams>>) => void;

export type UseGetSavedCardsInfiniteQueryReturnType = {
  requestMoreCards: RequestMoreCardsFunc;
  cancelGetCardsRequest: () => Promise<void>;
  resetAirCardsQuery: ResetCardsQueryFunc;
  setRequestFilterSync: SetRequestFilterSyncFunc;
  cards: AirSavedCardModel[];
  canRequestMoreAirCards: boolean;
  isCardsLoading: boolean;
  isCardsFetching: boolean;
  isCardsLoadingNextPage: boolean;
  countByAirStatus: CountByAirStatusModel[];
  refetchAirCards: () => void;
};

export type UseAirSavedCardsInfiniteQueryParams = Required<Pick<UseQueryOptions, "queryKey">> & {
  shouldRequestOnMount?: boolean | (() => boolean);
  onSuccess?: (params: {
    page: AirSavedCardModel[];
    pages: AirSavedCardModel[][];
    totalCount: number;
    countByStatus: CountByAirStatusModel[];
  }) => void;
  onSettled?: (params: {
    page: AirSavedCardModel[] | undefined;
    pages: AirSavedCardModel[][] | undefined;
    totalCount: number;
    error: SearchError | null;
  }) => void;
  onBeforeRequest?: () => void;
  initialFilter?: Nullable<Partial<RequestFuncParams>>;
};

export const useAirSavedCardsInfiniteQuery = ({
  onBeforeRequest,
  shouldRequestOnMount = true,
  queryKey,
  initialFilter,
  onSuccess,
  onSettled,
}: UseAirSavedCardsInfiniteQueryParams): UseGetSavedCardsInfiniteQueryReturnType => {
  const queryClient = useQueryClient();
  const requestFuncParamsRef = useRef<Nullable<Partial<RequestFuncParams>>>(initialFilter);
  const [enabled, setEnabled] = useState(false);

  const meta = useInfiniteQuery<SavedAirResponseDTO, SearchError>({
    queryKey,
    enabled: enabled,
    retry: false,
    queryFn: async ({ signal, pageParam = 0 }) => {
      onBeforeRequest?.();
      return await savedShipmentService.getCards(
        { limit: CARDS_REQUEST_LIMIT, offset: pageParam as number, ...requestFuncParamsRef.current },
        signal
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return false;
      const totalCount = lastPage.total_count;
      const currentCount = allPages.reduce((acc, page) => acc + page.cards.length, 0);
      if (currentCount >= totalCount) {
        return undefined;
      }
      return currentCount;
    },
  });

  const { data, fetchNextPage, hasNextPage = false, isLoading, isFetching, isFetchingNextPage, refetch } = meta;

  const { setRequestCallbacks } = useRequestCallbacks<InfiniteData<SavedAirResponseDTO>, SearchError>(meta);

  const requestCallbacks = () => {
    setRequestCallbacks({
      onSuccess: (response) => onSuccess?.(parseResponse(response.pages)),
      onSettled: (response, error) => {
        const { page, pages, totalCount } = parseResponse(response?.pages);
        onSettled?.({
          page,
          pages,
          totalCount,
          error,
        });
      },
    });
  };

  const requestMoreCards: RequestMoreCardsFunc = () => {
    requestCallbacks();
    fetchNextPage();
  };

  useEffect(() => {
    requestCallbacks();
    if (typeof shouldRequestOnMount === "function") {
      setEnabled(shouldRequestOnMount());
      return;
    }
    setEnabled(shouldRequestOnMount);
  }, [shouldRequestOnMount]);

  const setRequestFilterSync: SetRequestFilterSyncFunc = (filter) => {
    requestFuncParamsRef.current = filter;
  };

  const resetCardsQuery: ResetCardsQueryFunc = () => {
    setRequestFilterSync(null);
    requestCallbacks();
    return queryClient.resetQueries({ queryKey });
  };

  const refetchCards = () => {
    requestCallbacks();
    return refetch();
  };

  const cancelGetCardsRequest = () => {
    return queryClient.cancelQueries({ queryKey });
  };

  const cardsResponse = useMemo(() => {
    const { page } = parseResponse(data?.pages);
    return page ?? [];
  }, [data]);

  const countByStatus = useMemo(() => {
    const { countByStatus } = parseResponse(data?.pages);
    return countByStatus ?? [];
  }, [data]);

  return {
    requestMoreCards,
    cancelGetCardsRequest,
    resetAirCardsQuery: resetCardsQuery,
    setRequestFilterSync,
    cards: cardsResponse,
    refetchAirCards: refetchCards,
    canRequestMoreAirCards: hasNextPage,
    isCardsLoading: isLoading,
    isCardsFetching: isFetching,
    isCardsLoadingNextPage: isFetchingNextPage,
    countByAirStatus: countByStatus,
  };
};
